import { Injectable, computed, inject, signal } from '@angular/core';
import { Layer } from '../../store/cop/models/layer.model';
import { Dataset, Document } from '@app/modules/core/store/models';
import { DataApiService } from '../../services/data/data-api.service';
import { UserProfileFacade } from '@app/modules/core/store/user-profile';
import { LocalStorageService } from '@app/core/services';

export interface PopupOptions {
  position: google.maps.LatLng;
  offsetY?: number;
  offsetX?: number;
  offsetBelow?: number;
  layer: Layer;
  dataset: Dataset;
  document?: Document;
  isCluster?: boolean;
  markerData?: Record<string, any>;
}

export interface PopupRelatedData {
  layer: Layer;
  dataset: Dataset;
  document?: Document;
  isCluster?: boolean;
  markerData?: Record<string, any>;
}

export interface PopupState {
  screenPosition: {
    x: number;
    y: number;
    displayBelow: boolean;
  };
  isVisible: boolean;
  loading: boolean;
  relatedData?: PopupRelatedData;
}

const initialState: PopupState = {
  screenPosition: {
    x: 0,
    y: 0,
    displayBelow: false
  },
  isVisible: false,
  loading: false,
  relatedData: null
};

@Injectable({ providedIn: 'root' })
export class PopupService {
  private dataApiService = inject(DataApiService);
  private overlayView: google.maps.OverlayView;
  private timer: NodeJS.Timeout;
  private isSideNavEnabled = inject(UserProfileFacade).sideNavEnabled;
  private storage = inject(LocalStorageService);

  // state
  private state = signal<PopupState>(initialState);

  // selectors
  readonly screenPosition = computed(() => this.state().screenPosition);
  readonly isVisible = computed(() => this.state().isVisible);
  readonly relatedData = computed(() => this.state().relatedData);
  readonly loading = computed(() => this.state().loading);

  setMap(map: google.maps.Map): void {
    if (Boolean(this.overlayView)) {
      this.overlayView = null;
    }

    const overlayView = new google.maps.OverlayView();
    overlayView.draw = () => {};
    overlayView.setMap(map);
    this.overlayView = overlayView;
  }

  private getSideNavOffset(): number {
    if (this.isSideNavEnabled()) {
      const isExpanded = this.storage.getItem('topo_side-nav-expanded') ?? true;
      // width of side nav in pixels
      return isExpanded ? 230 : 64;
    }
    return 0;
  }

  open(options: PopupOptions): void {
    const {
      position = null,
      offsetY = 0,
      offsetX = 0,
      offsetBelow = 0,
      layer,
      dataset,
      document: partialDocument = null,
      markerData = null,
      isCluster = false
    } = options;

    if (!Boolean(position) || !Boolean(layer) || !Boolean(dataset) || !Boolean(this.overlayView)) {
      return;
    }

    this.timer = setTimeout(() => {
      const pointPosition = this.overlayView.getProjection().fromLatLngToContainerPixel(position);

      pointPosition.x += this.getSideNavOffset();

      pointPosition.y += offsetY;
      pointPosition.x += offsetX;

      // *NOTE - 72 is the height in pixels of the app header
      const displayBelow = pointPosition.y <= (window.innerHeight - 72) / 2;

      if (displayBelow) {
        pointPosition.y += offsetBelow;
      }

      this.state.set({
        loading: true,
        screenPosition: {
          ...pointPosition,
          displayBelow
        },
        isVisible: true,
        relatedData: {
          layer,
          dataset,
          document: partialDocument,
          isCluster,
          markerData
        }
      });

      if (Boolean(partialDocument) && !isCluster) {
        this.dataApiService.getDocument(dataset.uId, partialDocument.uid).subscribe(document => {
          this.state.update(state => {
            const updatedState = { ...state, loading: false };

            if (Boolean(state.relatedData) && state.relatedData?.document?.uid === document.uid) {
              updatedState.relatedData.document = document;
            }

            return updatedState;
          });
        });
      }
    }, 500);
  }

  close(): void {
    if (Boolean(this.timer)) {
      clearTimeout(this.timer);
    }

    if (this.isVisible()) {
      this.state.set(initialState);
    }
  }

  isEnabledForLayer(layer: Layer): boolean {
    return layer?.displayOptions?.popupsEnabled;
  }
}
